import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Plus, TrendingUp, TrendingDown, DollarSign, Trash2, Upload, FileText, Pencil, Check, X, Eye, Download } from 'lucide-react';
import { formatCurrency } from '../utils/currency';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import MixedExecuteDialog from '../components/MixedExecuteDialog';
import { downloadInvoicePDF } from '../utils/invoicePdf';
import { generatePaymentPlannerPDF, plannerToPlan } from '../utils/paymentPlannerPdf';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FinanceDetail = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeBook = searchParams.get('book') || 'freelance';
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({ openingBalance: 0, totalIncome: 0, totalExpense: 0, adjustments: 0, balance: 0 });
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [openingBalanceDialogOpen, setOpeningBalanceDialogOpen] = useState(false);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [reportMonth, setReportMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [editingTransactionName, setEditingTransactionName] = useState('');
  
  // Full edit dialog state — when set, the Add Transaction dialog is in "edit mode"
  const [editTxId, setEditTxId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    type: 'income',
    amount: '',
    projectId: '',
    teamMemberId: '',
    proofFile: null,
    proofFileName: '',
    paymentMethod: 'cash',
    transferFee: '2500',
    projectAllocations: [],
    postponedPaymentId: null,
    invoiceId: null
  });

  const [postponedCount, setPostponedCount] = useState(0);
  const [showPostponed, setShowPostponed] = useState(false);
  const [postponedList, setPostponedList] = useState([]);
  const [taxDebtsOpen, setTaxDebtsOpen] = useState(false);
  const [taxDebts, setTaxDebts] = useState([]);
  const [taxDebtCount, setTaxDebtCount] = useState(0);
  // Executed archives (dialogs opened from the middle toolbar bar)
  const [executedInvoicesOpen, setExecutedInvoicesOpen] = useState(false);
  const [executedInvoices, setExecutedInvoices] = useState([]);
  const [executedPlannersOpen, setExecutedPlannersOpen] = useState(false);
  const [executedPlanners, setExecutedPlanners] = useState([]);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  // Multi-book execute dialog state
  const [mixedDialogOpen, setMixedDialogOpen] = useState(false);
  const [mixedPlanner, setMixedPlanner] = useState(null);

  const [openingBalanceForm, setOpeningBalanceForm] = useState({
    date: '',
    amount: ''
  });

  const [adjustmentForm, setAdjustmentForm] = useState({
    date: '',
    adjustmentType: 'increase',
    amount: '',
    reason: '',
    proofFile: null,
    proofFileName: ''
  });

  const [filteredTeamMembers, setFilteredTeamMembers] = useState([]);

  useEffect(() => {
    fetchData();
    fetchPostponedCount();
    // Check for prefill from postponed payment execution
    const prefillParam = searchParams.get('prefill');
    if (prefillParam) {
      try {
        const prefill = JSON.parse(prefillParam);
        setFormData(prev => ({
          ...prev,
          name: prefill.name || '',
          date: new Date().toISOString().split('T')[0],
          type: prefill.type || 'expense',
          amount: prefill.amount || '',
          teamMemberId: prefill.teamMemberId || '',
          postponedPaymentId: prefill.postponedPaymentId || null,
          invoiceId: prefill.invoiceId || null,
          projectAllocations: prefill.projectAllocations || []
        }));
        setDialogOpen(true);
        // Clear prefill from URL so a refresh doesn't re-trigger the dialog
        const next = new URLSearchParams(searchParams);
        next.delete('prefill');
        setSearchParams(next, { replace: true });
      } catch {}
    }
  }, [activeBook]);

  const fetchTaxDebts = async () => {
    try {
      const r = await axios.get(`${API}/tax-debts?status=pending`);
      setTaxDebts(r.data || []);
      setTaxDebtCount((r.data || []).length);
    } catch { setTaxDebts([]); setTaxDebtCount(0); }
  };

  const openTaxDebts = async () => {
    await fetchTaxDebts();
    setTaxDebtsOpen(true);
  };

  const payTaxDebt = async (debt) => {
    if (!window.confirm(`Pay PPh ${formatCurrency(debt.taxAmount)} to authority?\n\n${debt.teamMemberName} · ${debt.projectName}\n\nThis will create a Company Book expense transaction.`)) return;
    try {
      await axios.post(`${API}/tax-debts/${debt.id}/pay`, {});
      toast.success('Tax paid — Company Book updated');
      await Promise.all([fetchTaxDebts(), fetchData(activeBook)]);
    } catch { toast.error('Failed to pay tax'); }
  };

  const deleteTaxDebt = async (debtId) => {
    if (!window.confirm('Delete this tax debt entry? This does NOT reverse the original team payment.')) return;
    try {
      await axios.delete(`${API}/tax-debts/${debtId}`);
      toast.success('Deleted');
      await fetchTaxDebts();
    } catch { toast.error('Failed to delete'); }
  };

  const fetchPostponedCount = async () => {    try {
      const res = await axios.get(`${API}/postponed-payments/count?book=${activeBook}`);
      setPostponedCount(res.data.count);
    } catch {}
  };

  const fetchPostponedList = async () => {
    try {
      const res = await axios.get(`${API}/postponed-payments?book=${activeBook}`);
      setPostponedList(res.data);
      setShowPostponed(true);
    } catch { toast.error('Failed to load'); }
  };

  const fetchExecutedInvoices = async () => {
    try {
      // All non-pending invoices in the current book. Executed = paid or partial.
      const res = await axios.get(`${API}/invoices?book=${activeBook}`);
      const executed = (res.data || []).filter(inv => inv.status && inv.status !== 'pending');
      setExecutedInvoices(executed);
      setExecutedInvoicesOpen(true);
    } catch { toast.error('Failed to load executed invoices'); }
  };

  const fetchExecutedPlanners = async () => {
    try {
      const res = await axios.get(`${API}/postponed-payments?book=${activeBook}&status=executed`);
      setExecutedPlanners(res.data || []);
      setExecutedPlannersOpen(true);
    } catch { toast.error('Failed to load executed planners'); }
  };

  useEffect(() => {
    // Filter team members based on selected project
    if (formData.projectId) {
      const project = projects.find(p => p.id === formData.projectId);
      if (project && project.teamMembers) {
        // Get team member IDs from the team list that match names in project
        const memberNames = project.teamMembers.map(tm => tm.name);
        const filtered = teamMembers.filter(tm => memberNames.includes(tm.name));
        setFilteredTeamMembers(filtered);
      } else {
        setFilteredTeamMembers([]);
      }
    } else {
      setFilteredTeamMembers([]);
    }
  }, [formData.projectId, projects, teamMembers]);

  const fetchData = async () => {
    try {
      const [transactionsRes, summaryRes, projectsRes, teamRes] = await Promise.all([
        axios.get(`${API}/transactions?book=${activeBook}`),
        axios.get(`${API}/finance/summary?book=${activeBook}`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/team`)
      ]);
      
      setTransactions(transactionsRes.data);
      setSummary(summaryRes.data);
      setProjects(projectsRes.data.filter(p => (p.financeBook || 'freelance') === activeBook));
      setTeamMembers(teamRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load financial data');
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        toast.error('Only JPG, PNG, and PDF files are allowed');
        return;
      }

      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          proofFile: reader.result,
          proofFileName: file.name
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.date || !formData.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    // No confirmation needed - user explicitly selected "None (General Expense)" option

    try {
      const hasAllocations = formData.projectAllocations.length > 0;

      // Validate: when allocations exist, sum of amounts must roughly equal the total amount
      if (hasAllocations) {
        const sum = formData.projectAllocations.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
        if (Math.abs(sum - parseFloat(formData.amount)) > 1) {
          toast.error(`Sum of project allocations (${formatCurrency(sum)}) must equal total amount (${formatCurrency(parseFloat(formData.amount))})`);
          return;
        }
      }

      // CREATE or EDIT — depends on editTxId
      if (editTxId) {
        await axios.put(`${API}/transactions/${editTxId}`, {
          name: formData.name,
          date: formData.date,
          type: formData.type,
          amount: parseFloat(formData.amount),
          projectId: hasAllocations ? null : (formData.projectId || null),
          teamMemberId: (formData.type === 'expense' && formData.teamMemberId) ? formData.teamMemberId : null,
          proofFile: formData.proofFile,
          proofFileName: formData.proofFileName,
          financeBook: activeBook,
          projectAllocations: formData.projectAllocations,
          // Bank-fee link is synced by the backend based on these two fields.
          paymentMethod: formData.paymentMethod || 'cash',
          transferFee: (formData.paymentMethod === 'check' && formData.transferFee)
            ? parseFloat(formData.transferFee) : 0,
        });
        toast.success(
          formData.paymentMethod === 'check'
            ? 'Transaction updated (bank transfer fee synced)'
            : 'Transaction updated successfully!'
        );
      } else {
        // Create main transaction — backend auto-creates the linked bank_fee child
        // if paymentMethod='check' and transferFee > 0.
        await axios.post(`${API}/transactions`, {
          name: formData.name,
          date: formData.date,
          type: formData.type,
          category: 'regular',
          amount: parseFloat(formData.amount),
          projectId: hasAllocations ? null : (formData.projectId || null),
          teamMemberId: (formData.type === 'expense' && formData.teamMemberId) ? formData.teamMemberId : null,
          proofFile: formData.proofFile,
          proofFileName: formData.proofFileName,
          financeBook: activeBook,
          projectAllocations: formData.projectAllocations,
          postponedPaymentId: formData.postponedPaymentId,
          invoiceId: formData.invoiceId,
          paymentMethod: formData.paymentMethod || 'cash',
          transferFee: (formData.paymentMethod === 'check' && formData.transferFee)
            ? parseFloat(formData.transferFee) : 0,
        });

        toast.success(formData.paymentMethod === 'check'
          ? 'Transaction and transfer fee added successfully!'
          : 'Transaction added successfully!');
      }
      setEditTxId(null);
      setDialogOpen(false);
      setFormData({
        name: '',
        date: '',
        type: 'income',
        amount: '',
        projectId: '',
        teamMemberId: '',
        proofFile: null,
        proofFileName: '',
        paymentMethod: 'cash',
        transferFee: '2500',
        projectAllocations: [],
        postponedPaymentId: null,
        invoiceId: null
      });
      fetchData();
      fetchPostponedCount();
      if (activeBook === 'company') fetchTaxDebts();
      else { setTaxDebts([]); setTaxDebtCount(0); }
    } catch (error) {
      console.error('Error saving transaction:', error);
      toast.error(editTxId ? 'Failed to update transaction' : 'Failed to add transaction');
    }
  };

  // Open the dialog in EDIT mode — pre-fills form from existing transaction
  const openEditTransaction = (tx) => {
    setEditTxId(tx.id);
    setFormData({
      name: tx.name || '',
      date: tx.date || '',
      type: tx.type || 'income',
      amount: tx.amount?.toString() || '',
      projectId: tx.projectId || '',
      teamMemberId: tx.teamMemberId || '',
      proofFile: tx.proofFile || null,
      proofFileName: tx.proofFileName || '',
      // Pre-fill the bank-transfer-fee state from the existing transaction so
      // toggling it on/off in the edit dialog stays intuitive.
      paymentMethod: tx.paymentMethod || 'cash',
      transferFee: tx.transferFee ? String(tx.transferFee) : '2500',
      projectAllocations: tx.projectAllocations || [],
      postponedPaymentId: tx.postponedPaymentId || null,
    });
    setDialogOpen(true);
  };

  const handleOpeningBalanceSubmit = async (e) => {
    e.preventDefault();
    
    if (!openingBalanceForm.date || !openingBalanceForm.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/transactions`, {
        name: 'Opening Balance',
        date: openingBalanceForm.date,
        type: 'income',
        category: 'opening_balance',
        amount: parseFloat(openingBalanceForm.amount),
        projectId: null,
        teamMemberId: null,
        financeBook: activeBook
      });
      
      toast.success('Opening balance set successfully!');
      setOpeningBalanceDialogOpen(false);
      setOpeningBalanceForm({ date: '', amount: '' });
      fetchData();
    } catch (error) {
      console.error('Error setting opening balance:', error);
      if (error.response?.status === 400) {
        toast.error('Opening balance already exists');
      } else {
        toast.error('Failed to set opening balance');
      }
    }
  };

  const handleAdjustmentSubmit = async (e) => {
    e.preventDefault();
    
    if (!adjustmentForm.date || !adjustmentForm.amount || !adjustmentForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/transactions`, {
        name: 'Rebalancing Adjustment',
        date: adjustmentForm.date,
        type: adjustmentForm.adjustmentType === 'increase' ? 'income' : 'expense',
        category: 'adjustment',
        amount: parseFloat(adjustmentForm.amount),
        adjustmentType: adjustmentForm.adjustmentType,
        adjustmentReason: adjustmentForm.reason,
        projectId: null,
        teamMemberId: null,
        proofFile: adjustmentForm.proofFile,
        proofFileName: adjustmentForm.proofFileName,
        financeBook: activeBook
      });
      
      toast.success('Adjustment recorded successfully!');
      setAdjustmentDialogOpen(false);
      setAdjustmentForm({
        date: '',
        adjustmentType: 'increase',
        amount: '',
        reason: '',
        proofFile: null,
        proofFileName: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error recording adjustment:', error);
      toast.error('Failed to record adjustment');
    }
  };

  const handleDeleteClick = (id, e) => {
    e.stopPropagation();
    setTransactionToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!transactionToDelete) return;

    try {
      await axios.delete(`${API}/transactions/${transactionToDelete}`);
      toast.success('✅ Successfully deleted');
      setDeleteDialogOpen(false);
      setTransactionToDelete(null);
      fetchData(); // This will refresh both transactions and summary
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error('⚠️ Deletion failed. Please try again later');
      setDeleteDialogOpen(false);
      setTransactionToDelete(null);
    }
  };

  const downloadProof = (transaction) => {
    if (transaction.proofFile) {
      const link = document.createElement('a');
      link.href = transaction.proofFile;
      link.download = transaction.proofFileName || 'proof';
      link.click();
    }
  };

  const startEditingTransaction = (e, transactionId, currentName) => {
    // Legacy fn — replaced by openEditTransaction (full Edit dialog).
    // Kept as no-op to avoid breakage if referenced elsewhere.
    e.stopPropagation();
    setEditingTransactionId(transactionId);
    setEditingTransactionName(currentName);
  };

  const cancelEditingTransaction = (e) => {
    e.stopPropagation();
    setEditingTransactionId(null);
    setEditingTransactionName('');
  };


  const generateMonthlyPDFReport = () => {
    try {
      const [year, month] = reportMonth.split('-');
      const monthName = new Date(year, month - 1).toLocaleString('en-US', { month: 'long' });
      
      // Filter transactions for selected month
      const monthlyTransactions = transactions.filter(t => {
        const transDate = new Date(t.date);
        return transDate.getFullYear() === parseInt(year) && 
               transDate.getMonth() === parseInt(month) - 1;
      }).sort((a, b) => new Date(a.date) - new Date(b.date));

      if (monthlyTransactions.length === 0) {
        toast.error('No transactions found for selected month');
        return;
      }

      // Calculate monthly totals
      let monthlyIncome = 0;
      let monthlyExpense = 0;
      
      monthlyTransactions.forEach(t => {
        if (t.type === 'income' || t.type === 'Opening Balance' || 
            (t.type === 'Rebalancing Adjustment' && t.adjustmentType === 'increase')) {
          monthlyIncome += t.amount;
        } else if (t.type === 'expense' || 
                   (t.type === 'Rebalancing Adjustment' && t.adjustmentType === 'decrease')) {
          monthlyExpense += t.amount;
        }
      });

      const monthlyBalance = monthlyIncome - monthlyExpense;

      // Create PDF
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Monthly Finance Report', 105, 20, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`${monthName} ${year}`, 105, 28, { align: 'center' });
      
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })}`, 105, 34, { align: 'center' });

      // Summary boxes
      const startY = 45;
      doc.setFillColor(240, 240, 240);
      doc.rect(15, startY, 180, 30, 'F');
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Monthly Summary', 20, startY + 8);
      
      doc.setFont('helvetica', 'normal');
      doc.text('Total Income:', 20, startY + 16);
      doc.text(`Rp ${monthlyIncome.toLocaleString('id-ID', { minimumFractionDigits: 0 })}`, 60, startY + 16);
      
      doc.text('Total Expense:', 20, startY + 23);
      doc.text(`Rp ${monthlyExpense.toLocaleString('id-ID', { minimumFractionDigits: 0 })}`, 60, startY + 23);
      
      doc.setFont('helvetica', 'bold');
      doc.text('Net Balance:', 120, startY + 20);
      const balanceColor = monthlyBalance >= 0 ? [0, 128, 0] : [255, 0, 0];
      doc.setTextColor(...balanceColor);
      doc.text(`Rp ${monthlyBalance.toLocaleString('id-ID', { minimumFractionDigits: 0 })}`, 160, startY + 20);
      doc.setTextColor(0, 0, 0);

      // Transactions table
      const tableData = monthlyTransactions.map(t => [
        t.date,
        t.name,
        t.type,
        t.projectName || '-',
        t.teamMemberName || '-',
        t.type === 'income' || t.type === 'Opening Balance' || 
        (t.type === 'Rebalancing Adjustment' && t.adjustmentType === 'increase')
          ? `+Rp ${t.amount.toLocaleString('id-ID', { minimumFractionDigits: 0 })}`
          : `-Rp ${t.amount.toLocaleString('id-ID', { minimumFractionDigits: 0 })}`
      ]);

      autoTable(doc, {
        startY: startY + 40,
        head: [['Date', 'Transaction', 'Type', 'Project', 'Team Member', 'Amount']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [66, 139, 202], fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 40 },
          2: { cellWidth: 25 },
          3: { cellWidth: 35 },
          4: { cellWidth: 30 },
          5: { cellWidth: 35, halign: 'right' }
        },
        didDrawPage: (data) => {
          // Footer
          doc.setFontSize(8);
          doc.setTextColor(128);
          doc.text(
            `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
            doc.internal.pageSize.width / 2,
            doc.internal.pageSize.height - 10,
            { align: 'center' }
          );
        }
      });

      // Save PDF
      doc.save(`Finance_Report_${monthName}_${year}.pdf`);
      toast.success('PDF report generated successfully!');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF report');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6" data-testid="finance-detail-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 relative">
          <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded absolute top-0 right-0">A.3</span>
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          {/* ═════════════════════════════════════════════════════════
                BAR 1 — Page title
                (Book name comes from the active book toggle below)
              ═════════════════════════════════════════════════════════ */}
          <div className="mb-4">
            <h1 className="text-4xl font-bold text-slate-800" style={{fontFamily: 'Work Sans, sans-serif'}}>
              Finance Details — <span className={activeBook === 'company' ? 'text-indigo-600' : 'text-purple-600'}>{activeBook === 'company' ? 'Company' : 'Freelance'}</span>
            </h1>
            <p className="text-slate-600 text-sm mt-1">Track income, expenses, planners, and invoices for this book</p>
          </div>

          {/* ═════════════════════════════════════════════════════════
                BAR 2 — Book toggle + Actions
                (Rebalance, Add Transaction, Executed Invoices, Executed Planners)
              ═════════════════════════════════════════════════════════ */}
          <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b">
            {/* Book Toggle */}
            <div className="flex rounded-lg border overflow-hidden shrink-0">
              <button
                onClick={() => setSearchParams({ book: 'freelance' })}
                className={`px-4 py-2 text-sm font-semibold transition ${activeBook === 'freelance' ? 'bg-purple-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                data-testid="book-freelance"
              >
                Freelance
              </button>
              <button
                onClick={() => setSearchParams({ book: 'company' })}
                className={`px-4 py-2 text-sm font-semibold transition ${activeBook === 'company' ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                data-testid="book-company"
              >
                Company
              </button>
            </div>

            <div className="h-6 w-px bg-slate-200 mx-1" />

            {/* Postponed (pending) — still on Bar 2 so it's easy to reach */}
            <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={fetchPostponedList} data-testid="postponed-planner-btn">
              📌 Postponed ({postponedCount})
            </Button>

            {activeBook === 'company' && (
              <Button variant="outline" size="sm" className="border-red-300 text-red-700 hover:bg-red-50" onClick={openTaxDebts} data-testid="tax-debts-btn">
                🧾 Tax Debts ({taxDebtCount})
              </Button>
            )}

            {/* Executed archives */}
            <Button variant="outline" size="sm" className="border-slate-300 text-slate-700 hover:bg-slate-50" onClick={() => setBreakdownOpen(true)} data-testid="breakdown-btn">
              🧮 Breakdown
            </Button>

            <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={fetchExecutedInvoices} data-testid="executed-invoices-btn">
              📑 Executed Invoices
            </Button>
            <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={fetchExecutedPlanners} data-testid="executed-planners-btn">
              🗂️ Executed Planners
            </Button>

            <div className="h-6 w-px bg-slate-200 mx-1" />

            {/* Money-affecting actions grouped on the right */}
            <div className="flex items-center gap-2 ml-auto">
              {summary.openingBalance === 0 && (
                <Dialog open={openingBalanceDialogOpen} onOpenChange={setOpeningBalanceDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="set-opening-balance-btn">
                      Set Opening Balance
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Set Opening Balance</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleOpeningBalanceSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="ob-date">Date</Label>
                        <Input
                          id="ob-date"
                          type="date"
                          data-testid="opening-balance-date-input"
                          value={openingBalanceForm.date}
                          onChange={(e) => setOpeningBalanceForm({...openingBalanceForm, date: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label htmlFor="ob-amount">Amount (Rp)</Label>
                        <Input
                          id="ob-amount"
                          type="number"
                          step="0.01"
                          data-testid="opening-balance-amount-input"
                          value={openingBalanceForm.amount}
                          onChange={(e) => setOpeningBalanceForm({...openingBalanceForm, amount: e.target.value})}
                          placeholder="Enter opening balance"
                        />
                      </div>
                      <p className="text-sm text-slate-600">
                        Note: Opening balance can only be set once and cannot be edited later.
                      </p>
                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setOpeningBalanceDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" data-testid="submit-opening-balance-btn">Set Opening Balance</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}

              <Dialog open={adjustmentDialogOpen} onOpenChange={setAdjustmentDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="add-adjustment-btn">
                    ⚖️ Rebalancing
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Rebalancing Adjustment</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAdjustmentSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="adj-date">Date</Label>
                      <Input
                        id="adj-date"
                        type="date"
                        data-testid="adjustment-date-input"
                        value={adjustmentForm.date}
                        onChange={(e) => setAdjustmentForm({...adjustmentForm, date: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="adj-type">Adjustment Type</Label>
                      <Select value={adjustmentForm.adjustmentType} onValueChange={(value) => setAdjustmentForm({...adjustmentForm, adjustmentType: value})}>
                        <SelectTrigger data-testid="adjustment-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="increase">Increase</SelectItem>
                          <SelectItem value="decrease">Decrease</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="adj-amount">Amount (Rp)</Label>
                      <Input
                        id="adj-amount"
                        type="number"
                        step="0.01"
                        data-testid="adjustment-amount-input"
                        value={adjustmentForm.amount}
                        onChange={(e) => setAdjustmentForm({...adjustmentForm, amount: e.target.value})}
                        placeholder="Enter amount"
                      />
                    </div>
                    <div>
                      <Label htmlFor="adj-reason">Reason for Adjustment *</Label>
                      <Input
                        id="adj-reason"
                        data-testid="adjustment-reason-input"
                        value={adjustmentForm.reason}
                        onChange={(e) => setAdjustmentForm({...adjustmentForm, reason: e.target.value})}
                        placeholder="Explain why this adjustment is needed"
                      />
                    </div>
                    <div>
                      <Label htmlFor="adj-proof">Upload Proof (Optional - JPG, PNG, or PDF)</Label>
                      <Input
                        id="adj-proof"
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setAdjustmentForm({
                                ...adjustmentForm,
                                proofFile: reader.result,
                                proofFileName: file.name
                              });
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        data-testid="adjustment-proof-input"
                        className="cursor-pointer"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setAdjustmentDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" data-testid="submit-adjustment-btn">Record Adjustment</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                if (!open) { setEditTxId(null); }
                setDialogOpen(open);
              }}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="add-transaction-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Transaction
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editTxId ? 'Edit Transaction' : 'Add New Transaction'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Transaction Name</Label>
                    <Input
                      id="name"
                      data-testid="transaction-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., Client payment, Material cost"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="date">Transaction Date</Label>
                      <Input
                        id="date"
                        type="date"
                        data-testid="transaction-date-input"
                        value={formData.date}
                        onChange={(e) => setFormData({...formData, date: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="type">Type</Label>
                      <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                        <SelectTrigger data-testid="transaction-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="income">Income</SelectItem>
                          <SelectItem value="expense">Expense</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="amount">Amount (Rp)</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      data-testid="transaction-amount-input"
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                      placeholder="Enter amount"
                      readOnly={formData.projectAllocations.length > 0}
                    />
                    {formData.projectAllocations.length > 0 && (
                      <p className="text-xs text-slate-500 mt-1 italic">
                        Total auto-calculated from project allocations below
                      </p>
                    )}
                  </div>

                  {/* Multi-project allocation section */}
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 bg-slate-50">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <Label className="text-sm font-semibold">Split across multiple projects</Label>
                        {formData.postponedPaymentId && (
                          <span className="ml-2 text-xs px-2 py-0.5 bg-amber-200 text-amber-900 rounded">
                            From Postponed Planner
                          </span>
                        )}
                        {formData.invoiceId && (
                          <span className="ml-2 text-xs px-2 py-0.5 bg-green-200 text-green-900 rounded">
                            Linked to Invoice ({formData.type === 'income' ? 'payment recording' : ''})
                          </span>
                        )}
                      </div>
                      {!formData.postponedPaymentId && !formData.invoiceId && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (formData.projectAllocations.length === 0) {
                              setFormData({
                                ...formData,
                                projectId: '',
                                projectAllocations: [{ projectId: '', projectName: '', percent: 0, amount: 0 }]
                              });
                            } else {
                              setFormData({ ...formData, projectAllocations: [] });
                            }
                          }}
                          data-testid="toggle-allocations-btn"
                        >
                          {formData.projectAllocations.length === 0 ? '+ Enable Split' : '× Disable Split'}
                        </Button>
                      )}
                    </div>

                    {formData.projectAllocations.length > 0 && (
                      <div className="space-y-2">
                        {formData.projectAllocations.map((alloc, idx) => (
                          <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-white p-2 rounded border" data-testid={`allocation-row-${idx}`}>
                            <div className="col-span-5">
                              <Select
                                value={alloc.projectId || ''}
                                onValueChange={(value) => {
                                  const proj = projects.find(p => p.id === value);
                                  const updated = [...formData.projectAllocations];
                                  updated[idx] = {
                                    ...updated[idx],
                                    projectId: value,
                                    projectName: proj?.name || ''
                                  };
                                  setFormData({ ...formData, projectAllocations: updated });
                                }}
                                disabled={!!formData.postponedPaymentId}
                              >
                                <SelectTrigger className="h-9 text-sm" data-testid={`allocation-project-${idx}`}>
                                  <SelectValue placeholder="Select project" />
                                </SelectTrigger>
                                <SelectContent>
                                  {projects.map(p => (
                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-3">
                              <Input
                                type="number"
                                placeholder="Amount"
                                value={alloc.amount || ''}
                                onChange={(e) => {
                                  const amt = parseFloat(e.target.value) || 0;
                                  const updated = [...formData.projectAllocations];
                                  updated[idx] = { ...updated[idx], amount: amt };
                                  // Recompute total amount
                                  const newTotal = updated.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
                                  // Recompute percents based on each project's contractValue
                                  updated.forEach(a => {
                                    const proj = projects.find(pp => pp.id === a.projectId);
                                    if (proj && proj.contractValue > 0) {
                                      a.percent = Math.round((a.amount / proj.contractValue) * 100);
                                    }
                                  });
                                  setFormData({
                                    ...formData,
                                    projectAllocations: updated,
                                    amount: newTotal.toString()
                                  });
                                }}
                                disabled={!!formData.postponedPaymentId}
                                className="h-9 text-sm text-right"
                                data-testid={`allocation-amount-${idx}`}
                              />
                            </div>
                            <div className="col-span-3 text-xs text-slate-600 text-center">
                              {alloc.percent ? `${alloc.percent}% of contract` : ''}
                            </div>
                            <div className="col-span-1">
                              {!formData.postponedPaymentId && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const updated = formData.projectAllocations.filter((_, i) => i !== idx);
                                    const newTotal = updated.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
                                    setFormData({
                                      ...formData,
                                      projectAllocations: updated,
                                      amount: updated.length > 0 ? newTotal.toString() : ''
                                    });
                                  }}
                                  className="h-8 w-8 p-0"
                                  data-testid={`remove-allocation-${idx}`}
                                >
                                  <X className="w-3 h-3 text-red-600" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                        {!formData.postponedPaymentId && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                projectAllocations: [
                                  ...formData.projectAllocations,
                                  { projectId: '', projectName: '', percent: 0, amount: 0 }
                                ]
                              });
                            }}
                            data-testid="add-allocation-row-btn"
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add Project
                          </Button>
                        )}
                        <div className="flex justify-end text-xs text-slate-600 pt-1 border-t">
                          Sum: <span className="font-bold ml-1">{formatCurrency(formData.projectAllocations.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0))}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="paymentMethod">Payment Method</Label>
                    <Select 
                      value={formData.paymentMethod} 
                      onValueChange={(value) => setFormData({...formData, paymentMethod: value})}
                    >
                      <SelectTrigger data-testid="payment-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="check">Check (Different Bank Transfer)</SelectItem>
                        <SelectItem value="same_bank">Same Bank Transfer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.paymentMethod === 'check' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <Label htmlFor="transferFee" className="text-yellow-900">Transfer Fee</Label>
                      <Select 
                        value={formData.transferFee} 
                        onValueChange={(value) => setFormData({...formData, transferFee: value})}
                      >
                        <SelectTrigger data-testid="transfer-fee-select" className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="2500">Rp 2.500 (Standard)</SelectItem>
                          <SelectItem value="6500">Rp 6.500 (Priority)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-yellow-700 mt-2">
                        ℹ️ A linked "Bank Transfer Fee - Different Bank" expense will be created automatically and stays linked — editing this transaction updates the fee, deleting removes both together (undoable with Ctrl+Z).
                      </p>
                    </div>
                  )}

                  {formData.projectAllocations.length === 0 && (
                  <div>
                    <Label htmlFor="project">Related Project (Optional)</Label>
                    <Select 
                      value={formData.projectId || "none"} 
                      onValueChange={(value) => setFormData({...formData, projectId: value === "none" ? "" : value, teamMemberId: ''})}
                    >
                      <SelectTrigger data-testid="transaction-project-select">
                        <SelectValue placeholder="Select project or leave blank for general expense" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          <span className="italic text-slate-600">None (General Expense)</span>
                        </SelectItem>
                        {projects.map(project => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.type === 'expense' && !formData.projectId && (
                      <p className="text-xs text-slate-500 mt-1 italic">
                        💡 General expense - not linked to any project
                      </p>
                    )}
                  </div>
                  )}

                  {formData.type === 'expense' && formData.projectAllocations.length > 0 && (
                    <div>
                      <Label htmlFor="teamMemberMulti">Team Member (Optional, applies to all projects)</Label>
                      <Select 
                        value={formData.teamMemberId} 
                        onValueChange={(value) => setFormData({...formData, teamMemberId: value})}
                      >
                        <SelectTrigger data-testid="transaction-member-multi-select">
                          <SelectValue placeholder="Select team member or leave blank" />
                        </SelectTrigger>
                        <SelectContent>
                          {teamMembers.map(member => (
                            <SelectItem key={member.id} value={member.id}>
                              {member.name} - {member.role}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {formData.type === 'expense' && formData.projectId && filteredTeamMembers.length > 0 && (
                    <div>
                      <Label htmlFor="teamMember">Related Team Member (Optional)</Label>
                      <Select 
                        value={formData.teamMemberId} 
                        onValueChange={(value) => setFormData({...formData, teamMemberId: value})}
                      >
                        <SelectTrigger data-testid="transaction-member-select">
                          <SelectValue placeholder="Select team member or leave blank" />
                        </SelectTrigger>
                        <SelectContent>
                          {filteredTeamMembers.map(member => (
                            <SelectItem key={member.id} value={member.id}>
                              {member.name} - {member.role}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1 italic">
                        💡 Leave blank if this expense is not for a specific team member
                      </p>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="proof">Upload Proof (JPG, PNG, or PDF - Max 5MB)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        id="proof"
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={handleFileChange}
                        data-testid="transaction-proof-input"
                        className="cursor-pointer"
                      />
                    </div>
                    {formData.proofFileName && (
                      <p className="text-sm text-green-600 mt-2">
                        File selected: {formData.proofFileName}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="submit-transaction-btn">{editTxId ? 'Save Changes' : 'Add Transaction'}</Button>
                  </div>
                </form>
              </DialogContent>
              </Dialog>
            </div>{/* /right cluster */}
          </div>{/* /Bar 2 */}

          {/* ═════════════════════════════════════════════════════════
                BAR 3 — Report month + Print PDF
              ═════════════════════════════════════════════════════════ */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-white">
              <Label htmlFor="report-month" className="text-sm text-slate-600 whitespace-nowrap">Report Month</Label>
              <Input
                id="report-month"
                type="month"
                value={reportMonth}
                onChange={(e) => setReportMonth(e.target.value)}
                className="w-40 h-8 border-0 p-0 focus-visible:ring-0"
                data-testid="report-month-input"
              />
            </div>
            <Button
              size="sm"
              onClick={generateMonthlyPDFReport}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="print-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Print PDF Report
            </Button>
          </div>{/* /Bar 3 */}
        </div>{/* /page header */}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 gap-3 mb-6">
          <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-purple-700">Opening Balance</p>
                <p className="text-base font-bold text-purple-800">{formatCurrency(summary.openingBalance)}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-green-700">Total Income</p>
                <p className="text-base font-bold text-green-800">{formatCurrency(summary.totalIncome)}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-red-700">Total Expense</p>
                <p className="text-base font-bold text-red-800">{formatCurrency(summary.totalExpense)}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-orange-700">Adjustments</p>
                <p className={`text-base font-bold ${summary.adjustments >= 0 ? 'text-orange-800' : 'text-red-800'}`}>
                  {formatCurrency(summary.adjustments)}
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-blue-700">Current Balance</p>
                <p className={`text-base font-bold ${summary.balance >= 0 ? 'text-blue-800' : 'text-red-800'}`}>
                  {formatCurrency(summary.balance)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Postponed Planner Panel */}
        {showPostponed && (
          <Card className="mb-6 border-amber-300 bg-amber-50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-amber-900">Postponed Payments</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowPostponed(false)}>Close</Button>
              </div>
            </CardHeader>
            <CardContent>
              {postponedList.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No postponed payments</p>
              ) : (
                <div className="space-y-3">
                  {postponedList.map(pp => (
                    <div key={pp.id} className="p-4 bg-white border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="font-medium">{pp.teamMemberName}</span>
                          <span className="ml-2 text-lg font-bold text-green-700">{formatCurrency(pp.totalAmount)}</span>
                          {(() => {
                            const books = pp.financeBooks && pp.financeBooks.length > 0
                              ? pp.financeBooks
                              : [pp.financeBook || 'freelance'];
                            return books.map(b => (
                              <span
                                key={b}
                                className={`ml-1 inline-block text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide text-white ${
                                  b === 'company' ? 'bg-red-500' : 'bg-blue-600'
                                }`}
                                data-testid={`planner-book-badge-${pp.id}-${b}`}
                              >
                                {b}
                              </span>
                            ));
                          })()}
                          {(pp.financeBooks && pp.financeBooks.length > 1) && (
                            <span className="ml-1 inline-block text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-300">
                              MIXED
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <span className="text-xs text-slate-500">{new Date(pp.createdAt).toLocaleDateString('id-ID')}</span>
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => {
                            // Detect multi-book planner and route to the dedicated dialog
                            const books = pp.financeBooks && pp.financeBooks.length > 0
                              ? pp.financeBooks
                              : [pp.financeBook || 'freelance'];
                            if (books.length > 1) {
                              setMixedPlanner(pp);
                              setMixedDialogOpen(true);
                              setShowPostponed(false);
                              return;
                            }
                            const params = new URLSearchParams({
                              book: activeBook,
                              prefill: JSON.stringify({
                                name: `Payment - ${pp.teamMemberName}`,
                                type: 'expense',
                                teamMemberId: pp.teamMemberId,
                                amount: pp.totalAmount,
                                postponedPaymentId: pp.id,
                                projectAllocations: pp.projects?.map(p => ({
                                  projectId: p.projectId, projectName: p.projectName,
                                  percent: p.paymentPercent, amount: p.amount
                                })) || []
                              })
                            });
                            setSearchParams(params);
                            setShowPostponed(false);
                            setFormData(prev => ({
                              ...prev,
                              name: `Payment - ${pp.teamMemberName}`,
                              type: 'expense',
                              amount: pp.totalAmount,
                              teamMemberId: pp.teamMemberId,
                              postponedPaymentId: pp.id,
                              projectAllocations: pp.projects?.map(p => ({
                                projectId: p.projectId, projectName: p.projectName,
                                percent: p.paymentPercent, amount: p.amount
                              })) || []
                            }));
                            setDialogOpen(true);
                          }} data-testid={`postponed-execute-${pp.id}`}>Execute</Button>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {pp.projects?.map((p, i) => (
                          <div key={i} className="flex justify-between text-sm text-slate-600">
                            <span>{p.projectName}</span>
                            <span>{p.paymentPercent}% → {formatCurrency(p.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Transactions Table */}
        <Card className="bg-white shadow-sm">
          <CardHeader>
            <CardTitle>All Transactions ({transactions.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-600 mb-4">No transactions yet. Add your first transaction!</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Transaction Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Team Member</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Proof</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions
                    .sort((a, b) => {
                      // First: sort by transaction date DESC (newest dates on top)
                      const dateDiff = new Date(b.date) - new Date(a.date);
                      if (dateDiff !== 0) return dateDiff;
                      // Tie-breaker: within the same date, sort by createdAt DESC
                      // (most recently entered transaction shows on top)
                      const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                      const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                      return bCreated - aCreated;
                    })
                    .map(transaction => (
                    <TableRow key={transaction.id} data-testid={`transaction-row-${transaction.id}`}>
                      <TableCell>{transaction.date}</TableCell>
                      <TableCell className="font-medium">
                        <span>{transaction.name}</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={transaction.type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {transaction.type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {transaction.projectAllocations && transaction.projectAllocations.length > 0 ? (
                          <div className="text-xs">
                            <span className="font-semibold text-purple-700">
                              Multi-project ({transaction.projectAllocations.length})
                            </span>
                            <div className="text-slate-500 mt-0.5">
                              {transaction.projectAllocations.map(a => a.projectName).filter(Boolean).join(', ')}
                            </div>
                          </div>
                        ) : (
                          <span 
                            className="text-blue-600 hover:underline cursor-pointer"
                            onClick={() => transaction.projectId && navigate(`/project/${transaction.projectId}`)}
                          >
                            {transaction.projectName || '-'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{transaction.teamMemberName || '-'}</TableCell>
                      <TableCell className={`font-bold ${transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                        {transaction.type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount)}
                      </TableCell>
                      <TableCell>
                        {transaction.proofFile ? (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const win = window.open('', '_blank');
                                if (transaction.proofFile.startsWith('data:application/pdf')) {
                                  win.document.write(`<iframe src="${transaction.proofFile}" style="width:100%;height:100%;border:none;"></iframe>`);
                                } else {
                                  win.document.write(`<img src="${transaction.proofFile}" style="max-width:100%;height:auto;" />`);
                                }
                              }}
                              data-testid={`view-proof-${transaction.id}`}
                            >
                              <Eye className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => downloadProof(transaction)}
                              data-testid={`download-proof-${transaction.id}`}
                            >
                              <Download className="w-4 h-4 text-slate-600" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); openEditTransaction(transaction); }}
                            data-testid={`edit-transaction-${transaction.id}`}
                            title="Edit transaction"
                          >
                            <Pencil className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDeleteClick(transaction.id, e)}
                            data-testid={`delete-transaction-${transaction.id}`}
                            title="Delete transaction"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={confirmDelete}
          title="Delete Transaction"
          description="Are you sure you want to delete this transaction? The finance balance will be updated immediately. This action cannot be undone."
        />

        {/* Balance Breakdown Dialog — shows the exact math behind Opening/Income/Expense/Adj/Balance */}
        <Dialog open={breakdownOpen} onOpenChange={setBreakdownOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="breakdown-dialog">
            <DialogHeader>
              <DialogTitle>🧮 Balance Breakdown — {activeBook === 'company' ? 'Company' : 'Freelance'}</DialogTitle>
            </DialogHeader>
            {(() => {
              const incomeTx = transactions.filter(t => t.type === 'income' && t.category !== 'opening_balance' && t.category !== 'adjustment');
              const expenseTx = transactions.filter(t => t.type === 'expense' && t.category !== 'adjustment');
              const grpBy = (rows) => {
                const m = {};
                rows.forEach(t => { const k = t.category || t.type; m[k] = (m[k] || 0) + (t.amount || 0); });
                return Object.entries(m).sort((a, b) => b[1] - a[1]);
              };
              const incomeByCat = grpBy(incomeTx);
              const expenseByCat = grpBy(expenseTx);
              return (
                <div className="space-y-3 text-sm">
                  <div className="bg-slate-50 border rounded-lg p-3">
                    <p className="font-semibold text-slate-800 mb-1">Math</p>
                    <div className="font-mono text-xs space-y-0.5 text-slate-700">
                      <div>Opening Balance = {formatCurrency(summary.openingBalance)}</div>
                      <div>+ Total Income = {formatCurrency(summary.totalIncome)}</div>
                      <div>− Total Expense = {formatCurrency(summary.totalExpense)}</div>
                      <div>+ Adjustments = {formatCurrency(summary.adjustments)}</div>
                      <div className="border-t pt-1 mt-1 font-bold">= Balance = {formatCurrency(summary.balance)}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="border rounded-lg p-3">
                      <p className="font-semibold text-emerald-700 mb-2">↑ Income by category</p>
                      {incomeByCat.length === 0 ? <p className="text-xs text-slate-400">None</p> : (
                        <div className="space-y-1">
                          {incomeByCat.map(([k, v]) => (
                            <div key={k} className="flex justify-between text-xs">
                              <span className="text-slate-600 truncate">{k}</span>
                              <span className="tabular-nums font-medium">{formatCurrency(v)}</span>
                            </div>
                          ))}
                          <div className="border-t pt-1 mt-1 flex justify-between text-xs font-bold text-emerald-800">
                            <span>Total Income</span><span className="tabular-nums">{formatCurrency(summary.totalIncome)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="border rounded-lg p-3">
                      <p className="font-semibold text-red-700 mb-2">↓ Expense by category</p>
                      {expenseByCat.length === 0 ? <p className="text-xs text-slate-400">None</p> : (
                        <div className="space-y-1">
                          {expenseByCat.map(([k, v]) => (
                            <div key={k} className="flex justify-between text-xs">
                              <span className="text-slate-600 truncate">{k}</span>
                              <span className="tabular-nums font-medium">{formatCurrency(v)}</span>
                            </div>
                          ))}
                          <div className="border-t pt-1 mt-1 flex justify-between text-xs font-bold text-red-800">
                            <span>Total Expense</span><span className="tabular-nums">{formatCurrency(summary.totalExpense)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 italic pt-1">
                    Note: these totals come from your transactions ledger, not from project contract values. Archived projects still contribute here — hiding a project on the Dashboard does NOT remove its past transactions.
                  </p>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Multi-Book Execute Dialog — only opens for planners spanning >1 book */}
        <MixedExecuteDialog
          planner={mixedPlanner}
          open={mixedDialogOpen}
          onOpenChange={setMixedDialogOpen}
          onSuccess={() => {
            setMixedPlanner(null);
            fetchData();
            fetchPostponedCount();
            if (activeBook === 'company') fetchTaxDebts();
          }}
        />

        {/* Tax Debts (PPh) Dialog */}
        <Dialog open={taxDebtsOpen} onOpenChange={setTaxDebtsOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="tax-debts-dialog">
            <DialogHeader>
              <DialogTitle>🧾 Tax Debts (PPh 5%) — Pending</DialogTitle>
            </DialogHeader>
            {taxDebts.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">No pending tax debts. PPh 5% is automatically deducted when a Company-book team payment reaches 100%.</p>
            ) : (
              <div className="space-y-2">
                {taxDebts.map(d => (
                  <div key={d.id} className="border rounded-lg p-3 bg-red-50 hover:bg-white transition" data-testid={`tax-debt-${d.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800">
                          {d.teamMemberName} <span className="text-slate-400">·</span> {d.projectName}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          Fee Pekerjaan: {formatCurrency(d.teamContractValue)} · Withheld: {new Date(d.createdAt).toLocaleDateString('id-ID')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-base font-bold text-red-700">{formatCurrency(d.taxAmount)}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => payTaxDebt(d)} data-testid={`pay-tax-${d.id}`}>
                          Pay
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteTaxDebt(d.id)}>
                          <Trash2 className="w-4 h-4 text-slate-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                <p className="text-[11px] text-slate-500 pt-2 border-t">
                  Total pending: <b>{formatCurrency(taxDebts.reduce((s, d) => s + (d.taxAmount || 0), 0))}</b>
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Executed Invoices Archive */}
        <Dialog open={executedInvoicesOpen} onOpenChange={setExecutedInvoicesOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="executed-invoices-dialog">
            <DialogHeader>
              <DialogTitle>📑 Executed Invoices — {activeBook === 'company' ? 'Company' : 'Freelance'}</DialogTitle>
            </DialogHeader>
            {executedInvoices.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">No executed invoices yet in this book.</p>
            ) : (
              <div className="space-y-2">
                {executedInvoices.map(inv => (
                  <div key={inv.id} className="border rounded-lg p-3 bg-slate-50 hover:bg-white transition" data-testid={`executed-invoice-${inv.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">{inv.invoiceNumber || inv.id.slice(0,8)}</span>
                          <span className="font-medium text-sm truncate">{inv.clientName || '—'}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-semibold ${
                            inv.status === 'paid' ? 'bg-green-100 text-green-800' :
                            inv.status === 'partial' ? 'bg-amber-100 text-amber-800' :
                            'bg-slate-100 text-slate-600'
                          }`}>{inv.status}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {inv.invoiceDate || (inv.createdAt || '').slice(0, 10)}
                          {' · '}
                          <span className="font-semibold text-slate-700">{formatCurrency(inv.totalAmount || 0)}</span>
                          {inv.paidPercent !== undefined && <> · Paid {Math.round(inv.paidPercent)}%</>}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        onClick={() => {
                          try {
                            downloadInvoicePDF(inv);
                            toast.success('Invoice PDF downloaded');
                          } catch { toast.error('Failed to generate PDF'); }
                        }}
                        data-testid={`reprint-invoice-${inv.id}`}
                      >
                        <Download className="w-3.5 h-3.5 mr-1" /> Reprint
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Executed Payment Planners Archive */}
        <Dialog open={executedPlannersOpen} onOpenChange={setExecutedPlannersOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="executed-planners-dialog">
            <DialogHeader>
              <DialogTitle>🗂️ Executed Payment Planners — {activeBook === 'company' ? 'Company' : 'Freelance'}</DialogTitle>
            </DialogHeader>
            {executedPlanners.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">No executed planners yet in this book.</p>
            ) : (
              <div className="space-y-2">
                {executedPlanners.map(pp => {
                  const books = pp.financeBooks && pp.financeBooks.length > 0 ? pp.financeBooks : [pp.financeBook || 'freelance'];
                  return (
                    <div key={pp.id} className="border rounded-lg p-3 bg-slate-50 hover:bg-white transition" data-testid={`executed-planner-${pp.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{pp.teamMemberName}</span>
                            {books.map(b => (
                              <span key={b} className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase text-white ${b === 'company' ? 'bg-red-500' : 'bg-blue-600'}`}>{b}</span>
                            ))}
                            {books.length > 1 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase bg-amber-100 text-amber-800 border border-amber-300">MIXED</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            {(pp.createdAt || '').slice(0, 10)}
                            {' · '}
                            <span className="font-semibold text-slate-700">{formatCurrency(pp.totalAmount || 0)}</span>
                            {' · '}
                            {(pp.projects || []).length} project{(pp.projects || []).length !== 1 ? 's' : ''}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-1 truncate">
                            {(pp.projects || []).map(p => `${p.projectName} (${p.paymentPercent}%)`).join(' · ')}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="shrink-0"
                          onClick={() => {
                            try {
                              generatePaymentPlannerPDF({
                                memberName: pp.teamMemberName,
                                plan: plannerToPlan(pp),
                                dateOverride: pp.createdAt,
                              });
                              toast.success('Kwitansi PDF downloaded');
                            } catch { toast.error('Failed to generate PDF'); }
                          }}
                          data-testid={`reprint-planner-${pp.id}`}
                        >
                          <Download className="w-3.5 h-3.5 mr-1" /> Reprint
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default FinanceDetail;